import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const orgNodeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  parentId: z.string().optional(),
  // Only used when the `color` prop is present (pillars); undefined otherwise.
  color: z.string().optional(),
  // Only used when the `head` prop is present (pillars). Null is "no head".
  headPersonId: z.string().nullable().optional(),
});

type OrgNodeFormValues = z.infer<typeof orgNodeSchema>;

interface OrgNodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dialog heading (e.g. "Create Pillar", "Edit Department"). */
  title: string;
  description?: string;
  /** Initial form values; pass undefined to start blank. */
  initial?: { name: string; description: string | null; parentId?: string };
  /** Optional parent picker — present for Department (pick pillar) and Role Team (pick department). */
  parent?: {
    label: string;
    options: Array<{ id: string; label: string }>;
    /** Required: pillarId for departments; departmentId for role teams. */
    required: boolean;
  };
  /**
   * Pillar and Department are synced from NOS1 and must always match it, so
   * their name (and a department's parent pillar) are read-only here — the
   * only way either changes is a NOS sync. Role Team has no NOS counterpart
   * and stays fully editable.
   */
  locked?: {
    name?: boolean;
    parent?: boolean;
    message: string;
  };
  /** Optional accent-color picker — present for Pillars only. */
  color?: {
    /** Current value: a named swatch key or a "#rrggbb" hex. */
    value: string;
    /** Named palette swatches to offer, each with a representative hex dot. */
    swatches: Array<{ key: string; swatch: string }>;
  };
  /**
   * Optional pillar-head picker — present for Pillars only. Unlike a team lead
   * this grants access, so the dialog says what the access is (EPIC-076 WI-284).
   */
  head?: { value: string | null };
  /** Submit handler — receives the form values. */
  onSubmit: (values: OrgNodeFormValues) => Promise<void> | void;
  isPending?: boolean;
  submitLabel?: string;
}

export function OrgNodeDialog({
  open,
  onOpenChange,
  title,
  description,
  initial,
  parent,
  locked,
  color,
  head,
  onSubmit,
  isPending,
  submitLabel = "Save",
}: OrgNodeDialogProps) {
  const form = useForm<OrgNodeFormValues>({
    resolver: zodResolver(orgNodeSchema),
    defaultValues: {
      name: initial?.name ?? "",
      description: initial?.description ?? "",
      parentId: initial?.parentId ?? parent?.options[0]?.id ?? "",
      color: color?.value,
      headPersonId: head?.value ?? null,
    },
  });

  async function handleSubmit(values: OrgNodeFormValues) {
    if (parent?.required && !values.parentId) {
      form.setError("parentId", { message: `${parent.label} is required` });
      return;
    }
    await onSubmit(values);
    form.reset();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {parent && (
              <FormField
                control={form.control}
                name="parentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{parent.label}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={locked?.parent}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={`Select ${parent.label.toLowerCase()}`} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {parent.options.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. Engineering" disabled={locked?.name} />
                  </FormControl>
                  {locked?.name && (
                    <p className="text-muted-foreground text-xs">{locked.message}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="Optional summary" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {head && (
              <FormField
                control={form.control}
                name="headPersonId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pillar head</FormLabel>
                    <EntityCombobox
                      entity="persons"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      filters={{ status: "active" }}
                      includeNone
                      noneLabel="No pillar head"
                      placeholder="No pillar head"
                      searchPlaceholder="Search people…"
                    />
                    <p className="text-muted-foreground text-xs">
                      Gives them the competency report for the roles in this pillar — totals only.
                      They see individual names only for people who report to them.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {color && (
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => {
                  const current = field.value ?? color.value;
                  const isCustom = current.startsWith("#");
                  const customValue = isCustom
                    ? current
                    : (color.swatches.find((s) => s.key === current)?.swatch ??
                      color.swatches[0]?.swatch ??
                      "#8b5cf6");
                  return (
                    <FormItem>
                      <FormLabel>Color</FormLabel>
                      <div className="flex flex-wrap items-center gap-2">
                        {color.swatches.map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => field.onChange(s.key)}
                            title={s.key}
                            aria-label={s.key}
                            aria-pressed={current === s.key}
                            className={cn(
                              "size-7 rounded-full border transition-transform hover:scale-110",
                              current === s.key
                                ? "ring-2 ring-ring ring-offset-2 ring-offset-background"
                                : "border-border",
                            )}
                            style={{ backgroundColor: s.swatch }}
                          />
                        ))}
                        {/* Custom hex — the native picker overlays a round
                            swatch. When empty it shows a "+" (add); once a
                            custom colour is chosen it fills with that colour
                            and carries a pencil badge so it's obvious the
                            swatch is still editable (re-openable). */}
                        <label
                          title={isCustom ? "Change custom color" : "Add custom color"}
                          className={cn(
                            "relative inline-flex size-7 cursor-pointer items-center justify-center rounded-full border text-muted-foreground transition-transform hover:scale-110 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background",
                            isCustom
                              ? "ring-2 ring-ring ring-offset-2 ring-offset-background"
                              : "border-dashed border-border",
                          )}
                          style={isCustom ? { backgroundColor: customValue } : undefined}
                        >
                          {isCustom ? (
                            <span className="pointer-events-none absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-sm">
                              <Pencil className="size-2.5" aria-hidden />
                            </span>
                          ) : (
                            <Plus className="size-3.5" aria-hidden />
                          )}
                          <input
                            type="color"
                            value={customValue}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="absolute inset-0 cursor-pointer opacity-0"
                            aria-label={isCustom ? "Change custom color" : "Add custom color"}
                          />
                        </label>
                      </div>
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        Pick a preset, or choose a custom color. The custom swatch stays editable —
                        click it again to change it.
                      </p>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving…" : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
